// ---------------------------------------------------------------------------
// BC-1586 — Offline Journal PWA: service-worker registration (Option A).
//
// Registers the root-scoped offline service worker ONLY on hosts in the
// `offlineSwHostAllowlist` config (branded-origin-gated containment). On any
// other host this is a no-op, so the default multi-app admin origin and other
// managed apps are never touched. Service-worker caches + IndexedDB are
// per-origin, so this fully isolates offline behavior to the branded origin.
//
// See managed-apps/business-coach/docs/specs/2026-07-11-offline-journal-pwa.spec.md
// ---------------------------------------------------------------------------
import AppConfig from 'shared/config-public';

const SW_URL = '/bc-offline-sw.js';

export function registerOfflineServiceWorker() {
	if (
		typeof window === 'undefined' ||
		typeof navigator === 'undefined' ||
		!('serviceWorker' in navigator)
	) {
		return;
	}

	const allowlist = Array.isArray(AppConfig.offlineSwHostAllowlist)
		? AppConfig.offlineSwHostAllowlist
		: [];
	const host = window.location.hostname;

	// Dev/testing override: ?bcOfflineSw=1 opts a non-allowlisted host in for
	// ONE session (replaces the old always-on 'localhost' allowlist entry that
	// caused the 2026-07-12 dev reload loop — cache-first /static/* pins stale
	// dev chunks under rsbuild's stable URLs → dev client hard-reloads forever).
	const devOverride =
		new URLSearchParams(window.location.search).get('bcOfflineSw') ===
		'1';

	// Branded-origin gate: on any non-allowlisted host, actively SELF-HEAL —
	// unregister a previously-installed offline SW and purge its shell caches
	// so browsers poisoned while 'localhost' was allowlisted recover on the
	// next successful page load (navigations are network-first, so this fresh
	// code always gets a chance to run even under a stale SW).
	if (!allowlist.includes(host) && !devOverride) {
		navigator.serviceWorker
			.getRegistrations()
			.then((registrations) => {
				registrations.forEach((registration) => {
					const scriptUrl =
						registration.active?.scriptURL ||
						registration.waiting?.scriptURL ||
						registration.installing?.scriptURL ||
						'';
					if (scriptUrl.includes('bc-offline-sw')) {
						registration.unregister();
						// eslint-disable-next-line no-console
						console.info(
							'[bc-offline-sw] unregistered (host not allowlisted)',
						);
					}
				});
			})
			.catch(() => {});
		if (window.caches && window.caches.keys) {
			window.caches
				.keys()
				.then((keys) => {
					keys.filter((k) => k.startsWith('bc-shell-')).forEach(
						(k) => window.caches.delete(k),
					);
				})
				.catch(() => {});
		}
		return;
	}

	const register = () => {
		navigator.serviceWorker
			.register(SW_URL, { scope: '/' })
			.then((registration) => {
				// eslint-disable-next-line no-console
				console.info(
					`[bc-offline-sw] registered (scope: ${registration.scope})`,
				);
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.warn('[bc-offline-sw] registration failed', err);
			});
	};

	// Register after load so SW install/precache never competes with first paint.
	if (document.readyState === 'complete') {
		register();
	} else {
		window.addEventListener('load', register, { once: true });
	}
}
